import React, { useEffect, useState, useContext, useMemo } from "react";
import AuthContext from "../../contexts/auth-context";
import useAxios from "../../hooks/useAxios";
import {
    Box,
    Typography,
    Pagination,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Paper,
} from "@mui/material";
import { toastify_status, PAGE_SIZE } from "../../components/config/variables";
import Toastify from "../../components/ui/Toastify";
import { ArrowDropUp, ArrowDropDown, ExpandMore, ExpandLess } from "@mui/icons-material";
import FilterModal from "../../components/filter/FilterModal";
import TuneIcon from '@mui/icons-material/Tune';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { motion } from 'framer-motion';
import { pageVariants, pageTransition } from "../../constants/GlobalStyles";
import { validateDispensedDrugs } from "../../Utils/validateHttpResponse";
import { useLocation } from "react-router-dom";
import { dateFormatter } from "../../Utils/DateFormatter";

const DispensingRecords = () => {
    const { operation } = useAxios();
    const { token } = useContext(AuthContext);
    const location = useLocation()
    const dispensedToday = location.state?.dispensedToday;

    const [filterOpen, setFilterOpen] = useState(false);

    // get todays date yyyy-mm-dd
    const getToday = () => new Date().toISOString().split('T')[0];
    const [fromDate, setFromDate] = useState(dispensedToday ? getToday() : '');
    const [toDate, setTodate] = useState(dispensedToday ? getToday() : '');
    const [patientFilter, setPatientFilter] = useState('');

    const [dispensedByFilter, setDipensedByFilter] = useState('');

    const [pharmacyList, setPharmacyList] = useState([])

    // used to pass to the filters modal to update the filters after delete a spesific filter
    const [newFiltersAfterDelete, setNewFiltersAfterDelete] = useState(undefined)

    const [dispensingRecords, setDispensingRecords] = useState([]);
    const [loading, setLoading] = useState(false);
    // pagination
    const [currentPage, setCurrentPage] = useState(1);

    // Sorting
    const [sortBy, setSortBy] = useState("dispensed_at");
    const [sortDirection, setSortDirection] = useState("asc")

    const [expanded, setExpanded] = useState([]); // ---> for expandable rows

    // for expandable rows
    const handleExpandClick = (id) => {
        setExpanded((prev) =>
            prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
        );
    };

    const handleSort = (columnKey) => {
        if (sortBy === columnKey) {
            setSortDirection(sortDirection === "asc" ? "desc" : "asc");
        } else {
            setSortBy(columnKey);
            setSortDirection("asc");
        }
    };

    const filteredData = useMemo(() => {
        // Grouping by patient_name and encounter
        const grouped = {};

        dispensingRecords.forEach((item) => {
            const key = `${item.patient_name}__${item.encounter}`;

            if (!grouped[key]) {
                grouped[key] = {
                    id: key,
                    patient_name: item.patient_name,
                    encounter: item.encounter,
                    patient_records: [],
                };
            }

            grouped[key].patient_records.push({
                drug_name: item.drug_name,
                quantity_dispensed: item.quantity_dispensed,
                price_per_unit: item.price_per_unit,
                total_cost: item.total_cost,
                dispensed_by_name: item.dispensed_by_name,
                dispensed_at: item.dispensed_at,
            });
        });

        let dataArray = Object.values(grouped);

        // Sorting
        dataArray.sort((a, b) => {
            let result = 0;

            const aVal = a[sortBy] || a.patient_records[0]?.[sortBy] || "";
            const bVal = b[sortBy] || b.patient_records[0]?.[sortBy] || "";

            if (!isNaN(aVal) && !isNaN(bVal)) {
                result = aVal - bVal;
            } else {
                result = String(aVal).localeCompare(String(bVal));
            }

            return sortDirection === "asc" ? result : -result;
        });

        return dataArray;
    }, [dispensingRecords, sortBy, sortDirection]);


    // Pagination
    const totalPages = Math.ceil(filteredData.length / PAGE_SIZE);
    const indexOfLastRow = currentPage * PAGE_SIZE;
    const indexOfFirstRow = indexOfLastRow - PAGE_SIZE;
    const currentRows = filteredData.slice(indexOfFirstRow, indexOfLastRow);

    useEffect(() => {
        setLoading(true);
        const params = new URLSearchParams();
        if (fromDate) {
            params.set('from_date', fromDate);
        }
        if (toDate) {
            params.set('to_date', toDate);
        }
        if (patientFilter) {
            params.set('patient_name', patientFilter);
        }
        if (dispensedByFilter) {
            params.set('dispensed_by', dispensedByFilter);
        }

        operation(
            {
                method: "GET",
                url: `api/pharmacy/dispensing/dispense?${params}`,
                headers: { Authorization: `token ${token}` },
            },
            (res) => {
                if(validateDispensedDrugs(res)) {
                    setDispensingRecords(res || []);
                } else {
                    Toastify({message: "Invalid response for dispensed medicines.", state: toastify_status.ERROR});
                }
                setLoading(false);
            },
            () => {
                setLoading(false);
                Toastify("Failed to load dispensed medicines.", toastify_status.ERROR);
            }
        );
        // eslint-disable-next-line
    }, [fromDate, toDate, patientFilter, dispensedByFilter]);

    const handleChangePage = (_, value) => {
        setCurrentPage(value);
    };

    const tableHeaders = [
        { label: "Patient Name", key: "patient_name", width: '22%' },
        { label: "Generic Name", key: "drug_name", width: '22%' },
        { label: "Dispensed By", key: "dispensed_by_name", width: '14%' },
        { label: "Dispensed At", key: "dispensed_at", width: '14%' },
        { label: "Quantity", key: "quantity_dispensed", width: '8%' },
        { label: "Unit Price", key: "price_per_unit", width: '9%' },
        { label: "Total Cost", key: "total_cost", width: '9%' },
    ]

    const handleApplyFilters = (selected) => {
        setFromDate(selected.fromDate);
        setTodate(selected.toDate);
        setPatientFilter(selected.patient);
        setDipensedByFilter(selected.dispensedBy);
        setCurrentPage(1);
    };

    const deleteSpesificFilter = (filterKey) => {
        const updatedFilters = {
            fromDate: fromDate,
            toDate: toDate,
            patient: patientFilter,
            dispensedBy: dispensedByFilter,
            [filterKey]: ''
        };

        handleApplyFilters(updatedFilters);
        setNewFiltersAfterDelete(updatedFilters);
    };

    const getPharmacyList = () => {
        operation(
            {
                method: "GET",
                url: `/api/user/pharmacyadminprofile/list_names`,
                headers: { Authorization: `token ${token}` },
            },
            (res) => {
                const normalized = res.map(item => ({
                    label: item.pharmacy_admin_name,
                    value: item.pharmacy_admin_name
                }));
                setPharmacyList(normalized || []);
            },
            () => {
                Toastify("Failed to load pharmacy list.", toastify_status.ERROR);
            }
        );
    }

    useEffect(() => {
        getPharmacyList();
        // eslint-disable-next-line
    }, [])

    return (
        <motion.div
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={pageTransition}>
            <Box>
                {/* Header */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                    }}
                >


                    <Typography sx={{ fontWeight: "500", fontSize: "20px" }}>
                        Dispensed Medicines
                    </Typography>
                </Box>

                <Box sx={{ mb: 2, display: 'flex' }}>
                    {/* Filters Button */}
                    <Box
                        onClick={() => setFilterOpen(true)}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            px: 2,
                            py: 1,
                            mr: 2,
                            borderRadius: "5px",
                            bgcolor: "primary.main",
                            color: "white",
                            cursor: "pointer",
                            transition: ".2s",
                            "&:hover": { opacity: 0.9 },
                        }}
                    >
                        <TuneIcon fontSize="small" />
                        <Typography fontWeight={600}>All Filters</Typography>
                    </Box>
                    {/* Active Filters */}
                    {[
                        { label: "From Date", value: fromDate, key: "fromDate" },
                        { label: "To Date", value: toDate, key: "toDate" },
                        { label: "Patient", value: patientFilter, key: "patient" },
                        { label: "Dispensed By", value: dispensedByFilter, key: "dispensedBy" },
                    ]
                        .filter(f => f.value)
                        .map(f => (
                            <Box
                                key={f.key}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    px: 2,
                                    py: 1,
                                    mr: 1,
                                    borderRadius: "10px",
                                    bgcolor: "background.default",
                                    border: "1px solid",
                                    borderColor: "divider",
                                    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                                }}
                            >
                                <Typography fontWeight={500}>
                                    {f.label}: {f.value}
                                </Typography>

                                <HighlightOffIcon
                                    onClick={() => deleteSpesificFilter(f.key)}
                                    sx={{
                                        cursor: "pointer",
                                        fontSize: 18,
                                        color: "text.secondary",
                                        "&:hover": { color: "error.main" },
                                    }}
                                />
                            </Box>
                        ))}
                </Box>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Paper elevation={0} sx={{ border: "1px solid #d9d9d9", borderRadius: '4px', overflow: 'hidden' }}>
                        <TableContainer sx={{ maxHeight: '555px' }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        {tableHeaders.map((header) => (
                                            <TableCell
                                                key={header.key}
                                                onClick={() => handleSort(header.key)}
                                                sx={{
                                                    backgroundColor: '#F5F5F5',
                                                    color: '#000000c7',
                                                    fontWeight: '500',
                                                    fontSize: '16px',
                                                    border: '1px solid #d9d9d9',
                                                    textAlign: 'center',
                                                    cursor: 'pointer',
                                                    py: 1.5,
                                                    width: header.width || '20%',
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    <Typography sx={{ fontWeight: "500", fontSize: "15px" }}>{header.label}</Typography>
                                                    {sortBy === header.key &&
                                                        (sortDirection === 'asc' ? <ArrowDropUp fontSize="small" /> : <ArrowDropDown fontSize="small" />)}
                                                </Box>
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {currentRows.map((patient) => (
                                        <React.Fragment key={patient.id}>
                                            {/* Parent Row */}
                                            <TableRow
                                                onClick={() => handleExpandClick(patient.id)}
                                                sx={{ backgroundColor: '#FCFEFE', cursor: "pointer", "&:hover": { backgroundColor: "#f9f9f9" } }}
                                            >
                                                <TableCell sx={{ border: '1px solid #d9d9d9', textAlign: 'left', px: 2 }}>
                                                    <Box sx={{ display: "flex", alignItems: "center" }}>
                                                        <IconButton size="small">
                                                            {expanded.includes(patient.id) ? <ExpandLess /> : <ExpandMore />}
                                                        </IconButton>
                                                        <Typography sx={{ fontWeight: "500", fontSize: "15px" }}>
                                                            {patient.patient_name}
                                                        </Typography>
                                                        <Typography sx={{
                                                            ml: "auto", bgcolor: "#FF0000", color: "white", px: 1, py: 0.2,
                                                            borderRadius: "12px", fontSize: "11px", fontWeight: "bold"
                                                        }}>
                                                            {patient.patient_records.length}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell colSpan={6} sx={{ border: '1px solid #d9d9d9' }} />
                                            </TableRow>

                                            {/* Expanded Rows */}
                                            {expanded.includes(patient.id) && patient.patient_records.map((rec, i) => (
                                                <TableRow key={i} sx={{ backgroundColor: "#fafafa" }}>
                                                    <TableCell sx={{ border: '1px solid #d9d9d9' }} />
                                                    <TableCell sx={{ border: '1px solid #d9d9d9', textAlign: 'center' }}>
                                                        <Typography sx={{ fontWeight: "400", fontSize: "15px" }}>
                                                            {rec.drug_name}
                                                        </Typography>
                                                    </TableCell>

                                                    <TableCell sx={{ border: '1px solid #d9d9d9', textAlign: 'center' }}>
                                                        <Typography sx={{ fontWeight: "400", fontSize: "15px" }}>
                                                            {rec.dispensed_by_name}
                                                        </Typography>
                                                    </TableCell>

                                                    <TableCell sx={{ border: '1px solid #d9d9d9', textAlign: 'center' }}>
                                                        <Typography sx={{ fontWeight: "400", fontSize: "15px" }}>
                                                            {dateFormatter(rec.dispensed_at)}
                                                        </Typography>
                                                    </TableCell>

                                                    <TableCell sx={{ border: '1px solid #d9d9d9', textAlign: 'center' }}>
                                                        <Typography sx={{ fontWeight: "400", fontSize: "15px" }}>
                                                            {rec.quantity_dispensed}
                                                        </Typography>
                                                    </TableCell>

                                                    <TableCell sx={{ border: '1px solid #d9d9d9', textAlign: 'center' }}>
                                                        <Typography sx={{ fontWeight: "400", fontSize: "15px" }}>
                                                            ${rec.price_per_unit}
                                                        </Typography>
                                                    </TableCell>

                                                    <TableCell sx={{ border: '1px solid #d9d9d9', textAlign: 'center' }}>
                                                        <Typography sx={{ fontWeight: "400", fontSize: "15px" }}>
                                                            ${rec.total_cost}
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </React.Fragment>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>

                )}

                {/* Pagination */}
                {!loading && totalPages > 1 && (
                    <Box display="flex" justifyContent="center" mt={2}>
                        <Pagination
                            count={totalPages}
                            page={currentPage}
                            onChange={handleChangePage}
                            color="primary"
                            disabled={loading}
                        />
                    </Box>
                )}

                {/* Filter Modal */}
                <FilterModal
                    open={filterOpen}
                    onClose={() => setFilterOpen(false)}
                    onApply={handleApplyFilters}
                    newFiltersAfterDelete={newFiltersAfterDelete}
                    initialFilters={{
                        fromDate,
                        toDate,
                        patient: patientFilter,
                        dispensedBy: dispensedByFilter,
                    }}
                    fields={["date", "patient"]}
                    customFields={[
                        {
                            name: 'dispensedBy',
                            label: 'Dsipensed By',
                            options: pharmacyList,
                        },
                    ]}
                />
            </Box>
        </motion.div>

    );
};

export default DispensingRecords;